import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ShieldCheck, ChevronRight, Facebook, Instagram, Linkedin, MapPin } from 'lucide-react';
import logo from '../assets/images/logo.png'; 
import FadeIn from './FadeIn';

const Footer = () => {
  const socialLinks = [
    { icon: <Facebook size={20} />, name: 'Facebook', url: '#' },
    { icon: <Instagram size={20} />, name: 'Instagram', url: '#' },
    { icon: <Linkedin size={20} />, name: 'Linkedin', url: '#' },
    { 
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path></svg>, 
      name: 'TikTok', 
      url: '#' 
    },
  ];

  return (
    <footer className="bg-slate-950 text-white pt-24 pb-12 px-8 relative overflow-hidden">
      {/* Subtle Background Glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#14b8a6]/5 rounded-full blur-[120px] -z-0"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          
          {/* 1. BRAND SECTION */}
          <FadeIn direction="right">
            <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-8">
              <Link to="/" className="inline-block">
                <img 
                  src={logo} 
                  alt="OptiRev Logo" 
                  className="h-16 w-auto object-contain brightness-0 invert opacity-100 transition-transform hover:scale-105" 
                  onError={(e) => { e.target.src = "https://via.placeholder.com/200x80?text=Logo+Missing"; }}
                />
              </Link>
              <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
                Merging established RCM strengths with specialized IT solutions to modernize 
                practice operations.
              </p>
              
              <div className="flex justify-center md:justify-start gap-4">
                {socialLinks.map((social, i) => (
                  <a 
                    key={i} 
                    href={social.url} 
                    className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-[#14b8a6] hover:border-[#14b8a6] hover:-translate-y-1 transition-all duration-300"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* 2. NAVIGATION */}
          <FadeIn delay={0.2}>
            <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-8">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="hidden md:block w-8 h-[2px] bg-[#14b8a6]"></span> Navigation
              </h4>
              <ul className="space-y-4 text-slate-400 text-sm">
                {['Home', 'Services', 'Why Us', 'Contact'].map((item) => (
                  <li key={item}>
                    <Link to={`/${item.toLowerCase().replace(' ', '-')}`} className="hover:text-[#14b8a6] flex items-center justify-center md:justify-start gap-2 group transition-colors">
                      <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform text-[#14b8a6]" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          {/* 3. CONTACT & LOCATION INFO */}
          <FadeIn delay={0.4}>
            <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-8">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="hidden md:block w-8 h-[2px] bg-[#14b8a6]"></span> Get In Touch
              </h4>
              <div className="space-y-6 text-sm">
                {/* USA Address */}
                <div className="flex flex-col md:flex-row items-center gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-[#14b8a6] group-hover:bg-[#14b8a6] group-hover:text-white transition-all">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase font-bold">USA Office</p>
                    <p className="text-slate-200 font-medium leading-tight">
                      9436 W. Lake Mead Blvd <br /> Ste 5 PMB 1034 <br /> Las Vegas, NV 89134
                    </p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-[#14b8a6] group-hover:bg-[#14b8a6] group-hover:text-white transition-all">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase font-bold">Call Us</p>
                    <p className="text-slate-200 font-bold">+19707840508</p>
                  </div>
                </div>
                
                <div className="flex flex-col md:flex-row items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-[#14b8a6] group-hover:bg-[#14b8a6] group-hover:text-white transition-all">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase font-bold">Email Us</p>
                    <p className="text-slate-200 font-bold">info@optirevhealthcare.com</p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* 4. COMPLIANCE */}
          <FadeIn delay={0.6} direction="left">
            <div className="flex flex-col items-center md:items-start text-center md:text-left bg-slate-900/50 p-8 rounded-[2.5rem] border border-slate-800 hover:border-[#14b8a6]/30 transition-colors">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="text-[#14b8a6]" size={28} />
                <h4 className="font-bold text-white">Trust & Security</h4>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-8">
                Strict adherence to HIPAA and HITECH standards.
              </p>
              <div className="flex justify-center md:justify-start gap-2">
                 <span className="text-[10px] font-black tracking-widest bg-slate-800 text-slate-300 px-3 py-1 rounded-md border border-slate-700">HIPAA</span>
                 <span className="text-[10px] font-black tracking-widest bg-slate-800 text-slate-300 px-3 py-1 rounded-md border border-slate-700">SOC2</span>
              </div>
            </div>
          </FadeIn>

        </div>

        {/* BOTTOM COPYRIGHT BAR */}
        <div className="border-t border-slate-900 pt-10 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-xs text-center md:text-left">
          <p>© 2026 OptiRev Healthcare. One partner for practice health.</p>
          <div className="flex gap-8">
            <span className="hover:text-white transition-colors cursor-pointer">Privacy Policy</span>
            <span className="hover:text-white transition-colors cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;